<template>
  <div>
    <card>
      <bookings-calendar
        :bookings="bookings"
        :date="date"
        @date-change="handleDateChange"
        @view-change="handleViewChange"
        @booking-click="handleBookingClick"
      ></bookings-calendar>
    </card>
    <modal :show.sync="isSelectedBookingModalVisible">
      <h4 slot="header" class="title title-up">Manage Booking</h4>
      <div v-if="selectedBooking">
        <booking-summary-table
          :booking="selectedBooking"
        ></booking-summary-table>
        <div class="d-flex justify-content-between">
          <span class="text-muted">Reference Number</span>
          <strong>{{ selectedBooking._id }}</strong>
        </div>
        <hr class="mt-1 mb-1" />
        <div>
          <div class="d-flex justify-content-between">
            <span class="text-muted">Booking Status</span>
            <div>
              <b-badge
                v-if="selectedBooking.bookingStatus === 'CONFIRMED'"
                id="selectedBooking__bookingStatus__confirmed"
                variant="info"
              >
                Confirmed
              </b-badge>
              <b-badge
                v-if="selectedBooking.bookingStatus === 'CANCELLED'"
                id="selectedBooking__bookingStatus__cancelled"
                variant="danger"
              >
                Cancelled
              </b-badge>
            </div>
          </div>
        </div>
        <hr class="mt-1 mb-1" />
        <div>
          <div class="d-flex justify-content-between">
            <span class="text-muted">Payment Status</span>
            <div>
              <b-badge
                v-if="selectedBooking.paymentStatus === 'PAID'"
                id="selectedBooking__paymentStatus__paid"
                variant="info"
              >
                Paid
              </b-badge>
              <b-badge
                v-if="selectedBooking.paymentStatus === 'NOT_PAID'"
                id="selectedBooking__paymentStatus__notPaid"
                variant="danger"
              >
                Not Paid
              </b-badge>
            </div>
          </div>
          <n-button
            v-if="
              selectedBooking.bookingStatus !== 'CANCELLED' &&
              selectedBooking.paymentStatus === 'NOT_PAID'
            "
            type="primary"
            @click.native="showMarkBookingAsPaidConfirmationModal"
          >
            Mark as Paid
          </n-button>
        </div>
        <hr class="mt-1 mb-1" />
        <br />
        <h6>{{ $t("message.bookingCustomerDetails") }}</h6>
        <div class="d-flex justify-content-between">
          <span class="text-muted">Name</span>
          <strong>{{ selectedBooking.customer.name }}</strong>
        </div>
        <hr class="mt-1 mb-1" />
        <div class="d-flex justify-content-between">
          <span class="text-muted">Phone</span>
          <strong>{{ selectedBooking.customer.phoneNumber }}</strong>
        </div>
        <hr class="mt-1 mb-1" />
        <div class="d-flex justify-content-between">
          <span class="text-muted">Email</span>
          <strong>{{ selectedBooking.customer.emailAddress }}</strong>
        </div>
        <hr class="mt-1 mb-1" />
        <div v-if="selectedBooking.customer.shortAddress">
          <div class="d-flex justify-content-between">
            <span class="text-muted">Address</span>
            <strong>{{ selectedBooking.customer.shortAddress }}</strong>
          </div>
          <hr class="mt-1 mb-1" />
        </div>
        <div v-if="selectedBooking.notes">
          <br />
          <h6>{{ $t("message.bookingNotes") }}</h6>
          <p class="text-muted">{{ selectedBooking.notes }}</p>
        </div>
      </div>
      <div slot="footer">
        <n-button
          v-if="
            selectedBooking && selectedBooking.bookingStatus !== 'CANCELLED'
          "
          class="mr-1"
          type="danger"
          @click.native="showCancelBookingConfirmationModal"
        >
          Cancel Booking
        </n-button>
        <n-button type="primary" @click.native="handleBookAgainClick()">
          {{ $t("message.bookAgainButton") }}
        </n-button>
      </div>
    </modal>
    <modal
      type="mini"
      :show.sync="isSelectedBookingCancelModalVisible"
      :show-close="false"
      :closable="false"
    >
      <h4 slot="header" class="title title-up">Are you sure?</h4>
      <n-button
        type="danger"
        block
        :busy="isCancelBookingLoading"
        :disabled="isCancelBookingLoading"
        @click.native="doCancelBooking"
      >
        {{ $t("message.continue") }}
      </n-button>
      <n-button
        type="secondary"
        simple
        block
        :disabled="isCancelBookingLoading"
        @click.native="goBackFromCancelBooking"
      >
        {{ $t("message.cancel") }}
      </n-button>
      <div slot="footer"></div>
    </modal>
    <modal
      type="mini"
      :show.sync="isSelectedBookingMarkAsPaidModalVisible"
      :show-close="false"
      :closable="false"
    >
      <h4 slot="header" class="title title-up">Are you sure?</h4>
      <n-button
        type="info"
        block
        :busy="isMarkBookingAsPaidLoading"
        :disabled="isMarkBookingAsPaidLoading"
        @click.native="doMarkBookingAsPaid"
      >
        {{ $t("message.continue") }}
      </n-button>
      <n-button
        type="secondary"
        simple
        block
        :disabled="isMarkBookingAsPaidLoading"
        @click.native="goBackFromMarkBookingAsPaid"
      >
        {{ $t("message.cancel") }}
      </n-button>
      <div slot="footer"></div>
    </modal>
  </div>
</template>
<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import * as Time from "@/helpers/time";
import { Modal } from "@/components";
import BookingsCalendar from "./BookingsCalendar";
import BookingSummaryTable from "./BookingSummaryTable";

export default {
  components: {
    Modal,
    BookingsCalendar,
    BookingSummaryTable,
  },
  data() {
    return {
      date: Time.currentDate(),
      /** Tracks FullCalendar view so date changes load the right range */
      calendarView: "resourceTimeGridDay",
      selectedBooking: null,
      isSelectedBookingModalVisible: false,
      isSelectedBookingCancelModalVisible: false,
      isCancelBookingLoading: false,
      isSelectedBookingMarkAsPaidModalVisible: false,
      isMarkBookingAsPaidLoading: false,
    };
  },
  methods: {
    ...mapActions({
      subscribeBookings: "bookings/subscribeBookings",
      fetchBookings: "bookings/fetchBookings",
      unsubscribeBookings: "bookings/unsubscribeBookings",
      cancelBooking: "bookings/cancelBooking",
      markBookingAsPaid: "bookings/markBookingAsPaid",
    }),
    setUrlDate(date) {
      const url = this.$route.path.replace(/overview.*/, `overview/${date}`);
      window.history.replaceState(null, "", url);
    },
    subscribeBookingsForDate() {
      const dateFrom = moment(this.date, Time.DATE_FORMAT_CODE)
        .subtract(1, "day")
        .format();
      const dateTo = moment(this.date, Time.DATE_FORMAT_CODE)
        .add(1, "day")
        .format();

      this.subscribeBookings({ dateFrom, dateTo });
    },
    fetchBookingsForMonth() {
      // Stop narrow day listener so it cannot overwrite month results
      this.unsubscribeBookings();

      const dateFrom = moment(this.date, Time.DATE_FORMAT_CODE)
        .startOf("month")
        .format();
      const dateTo = moment(this.date, Time.DATE_FORMAT_CODE)
        .endOf("month")
        .add(1, "day")
        .format();
      this.fetchBookings({ dateFrom, dateTo });
    },
    handleDateChange(date) {
      this.date = date;
      if (this.calendarView === "dayGridMonth") {
        this.fetchBookingsForMonth();
      } else {
        this.subscribeBookingsForDate();
      }
    },
    handleViewChange(view) {
      this.calendarView = view;
      if (view === "dayGridMonth") {
        this.fetchBookingsForMonth();
      } else {
        this.subscribeBookingsForDate();
      }
    },
    handleBookingClick(booking) {
      this.selectedBooking = booking;
      this.isSelectedBookingModalVisible = true;
    },
    showCancelBookingConfirmationModal() {
      this.isSelectedBookingModalVisible = false;
      this.isSelectedBookingCancelModalVisible = true;
    },
    goBackFromCancelBooking() {
      this.isSelectedBookingCancelModalVisible = false;
      this.isSelectedBookingModalVisible = true;
    },
    async doCancelBooking() {
      this.isCancelBookingLoading = true;
      try {
        await this.cancelBooking(this.selectedBooking);
        this.isCancelBookingLoading = false;
        this.isSelectedBookingCancelModalVisible = false;
        this.isSelectedBookingModalVisible = false;
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: "Booking cancelled.",
        });
      } catch (e) {
        this.isCancelBookingLoading = false;
        this.goBackFromCancelBooking();
        this.$notify({
          type: "error",
          title: this.$t("message.error"),
          text: (e && e.message) || String(e) || "Could not cancel booking.",
        });
      }
    },
    showMarkBookingAsPaidConfirmationModal() {
      this.isSelectedBookingModalVisible = false;
      this.isSelectedBookingMarkAsPaidModalVisible = true;
    },
    goBackFromMarkBookingAsPaid() {
      this.isSelectedBookingMarkAsPaidModalVisible = false;
      this.isSelectedBookingModalVisible = true;
    },
    async doMarkBookingAsPaid() {
      this.isMarkBookingAsPaidLoading = true;
      try {
        await this.markBookingAsPaid(this.selectedBooking);
        this.selectedBooking = {
          ...this.selectedBooking,
          paymentStatus: "PAID",
        };
        this.isMarkBookingAsPaidLoading = false;
        this.goBackFromMarkBookingAsPaid();
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: "Booking marked as paid.",
        });
      } catch (e) {
        this.isMarkBookingAsPaidLoading = false;
        this.goBackFromMarkBookingAsPaid();
        this.$notify({
          type: "error",
          title: this.$t("message.error"),
          text:
            (e && e.message) || String(e) || "Could not mark booking as paid.",
        });
      }
    },
    async handleBookAgainClick() {
      this.isSelectedBookingModalVisible = false;
      await new Promise((r) => setTimeout(r, 200));
      this.$router.push(
        `/bookings/add?populate-with=${this.selectedBooking._id}`
      );
    },
  },
  computed: mapGetters({
    bookings: "bookings/bookingsConfirmed",
  }),
  created() {
    const { date } = this.$route.params;

    if (!date) {
      this.setUrlDate(this.date);
    } else {
      this.date = date;
    }

    this.subscribeBookingsForDate();
  },
  beforeDestroy() {
    this.unsubscribeBookings();
  },
  watch: {
    date(value) {
      this.setUrlDate(value);
    },
  },
};
</script>
